<template>
  <v-footer
    fixed
    app
    inset
    :height="theme.footer_height"
    :color="theme.footer_bg_color"
  >
    <template v-if="mode.isDinein && ((mode.paymentMode === 0 && allPaymentModesLength === 0) || mode.paymentMode === 1)">
      <template v-if="isMember">
        <v-btn
          v-if="allowCheckout.all"
          block
          dark
          large
          class="btn"
          :color="theme.btn_color_secondary"
          @click="submit"
        >
          <v-layout row>
            <v-flex
              xs12
              class="text-xs-center"
            >
              {{ isMember ? $t('member_submit') : $t('guest_submit') }}
            </v-flex>
          </v-layout>
        </v-btn>
      </template>
      <template v-else>
        <v-btn
          v-if="allowLogin"
          block
          dark
          large
          class="btn pl-0 pr-0 text-xs-center"
          :color="theme.btn_color_secondary"
          @click="register"
        >
          <v-layout row>
            <v-flex
              xs12
              class="text-xs-center nowrap-text"
            >
              {{ registerBtn }}
            </v-flex>
          </v-layout>
        </v-btn>
        <v-btn
          v-if="allowCheckout.guest"
          block
          dark
          large
          class="btn pl-0 pr-0 text-xs-center"
          :color="theme.btn_color_secondary"
          @click="submit"
        >
          <v-layout row>
            <v-flex
              xs12
              class="text-xs-center"
            >
              {{ $t('guest_submit') }}
            </v-flex>
          </v-layout>
        </v-btn>
      </template>
    </template>
    <template v-else>
      <v-btn
        block
        :dark="!btnDisabled"
        large
        class="btn pl-0 pr-0 text-xs-center"
        :color="theme.btn_color_secondary"
        :disabled="btnDisabled"
        @click="checkout"
      >
        <v-layout row>
          <v-flex
            xs12
            class="text-xs-center"
          >
            {{ $t('proceed_to_checkout') }}
          </v-flex>
        </v-layout>
      </v-btn>
    </template>
  </v-footer>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import { checkAvailableSchema } from '../store/modules/menu'

export default {
  computed: {
    ...mapGetters('config', [
      'theme', 'enableZeroPrice', 'mode', 'enableMemberPoints', 'posType',
      'paymentModes', 'noPaymentModes', 'allowCheckout', 'allowLogin',
      'allPaymentModesLength', 'rid', 'holidays', 'gid'
    ]),
    ...mapGetters('auth', [
      'authenticated'
    ]),
    ...mapGetters('menu', [
      'catListIds'
    ]),
    ...mapGetters('cart', [
      'cart', 'cartAmount', 'pickupDate', 'pickupTime', 'cartMsg', 'vouchers','discountInfo',
      'discountInfoIsFilled',
    ]),
    ...mapGetters('order', [
      'orderMsg', 'paymentMode'
    ]),
    ...mapGetters('member', [
      'isMember', 'profile'
    ]),
    ...mapGetters('delivery', [
      'deliveryMsg', 'address'
    ]),
    ...mapGetters('voucher', [
      'discounts'
    ]),
    ...mapGetters('dine_mode', [
      'dineTimes',
    ]),
    btnDisabled () {
      return !this.mode.isDinein && !this.pickupTime;
    },
    registerBtn () {
      if (this.enableMemberPoints) {
        return this.$t('signup_earn_points')
      }
      return this.$t('signup')
    }
  },
  methods: {
    register () {
      if (this.authenticated) {
        this.$router.push({ name: 'register2' })
      } else {
        this.$router.push({ name: 'register' })
      }
    },
    async checkout () {
      if (this.mode.isDinein && Array.isArray(this.dineTimes) && this.dineTimes[0] === null) {
        await this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to submit',
          text: 'Our last order for the day has ended. If there\'s any enquiries please approach any of our staff. Thank you',
          modal: true
        })

        return;
      }

      if (this.rInfoExpiredAlert()) {
        return
      } else if (this.noTableOrAddressAlert()) {
        return
      }
      if (this.shouldExpireSession()) {
        await this.$store.dispatch('responseMessage', {
          type: 'error',
          text: 'Session Expired',
          noBtn: this.$mq === 'sm' || this.$mq === 'md' || this.$mq === 'lg' || this.$mq === 'xl'
        })
        await this.$store.dispatch('resetAppRelatedData')
        await this.$store.dispatch('config/resetModeSelected')
        if (this.isMember) {
          await this.$store.dispatch('fetchMembershipInfo')
        }
        await this.$router.push({ name: 'welcome' })
      }

      // This function might takes long to load, so add a loading indicator
      this.$bus.$emit('showFullLoading', true)

      if (parseFloat(this.cartAmount) === 0 && !this.enableZeroPrice) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Cart amount is zero',
          text: 'Your cart amount is zero. Please add at least 1 non-free item to your basket',
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }

      if (this.mode.isDelivery) {
        if (!this.address.floor_unit) {
          this.$bus.$emit('showAddressSelector', true)
          this.$bus.$emit('showFullLoading', false)
          return
        }
        // Refetch delivery plu
        await this.$store.dispatch('delivery/fetchDeliveryPlu')
        if (this.deliveryMsg) {
          this.$store.dispatch('responseMessage', {
            type: 'error',
            text: this.deliveryMsg,
            title: 'Unable to checkout',
            modal: true,
          })
          this.$bus.$emit('showFullLoading', false)
          return
        }
      }

      // Check availability schema
      await this.$store.dispatch('menu/fetchMenuCats')
      const notAvailableItems = []
      this.cart.forEach(item => {
        if (!item.is_promo && !this.catListIds.includes(item.category_id) && !item.member_voucher_id) {
          notAvailableItems.push(item.name)
        }
      })

      for (const item of this.cart) {
        const currentMode = this.mode
        const date = currentMode.isTakeaway || currentMode.isDelivery ? this.pickupDate : null
        const time = currentMode.isTakeaway || currentMode.isDelivery ? this.pickupTime : null
        const selectedDateTime = date && time ? date + ' ' + time : null
        const timeData = await axios.post('/api/moment/current', {
          holidays: this.holidays,
          date: selectedDateTime
        })
        const currentTime = timeData.data
        const setCourseItems = item.set_course_items.length > 0 ? item.set_course_items : null
        await axios.post('/api/menu/cat/items', {
          restaurant_id: parseInt(this.rid),
          category_id: item.category_id,
          date: date,
          time: time
        }).then(response => {
          if (!response.data.find(i => i.item_id === item.item_id)) {
            notAvailableItems.push(item.name)
          }
          else {
            let category_item = response.data.find(i => i.item_id === item.item_id)
            // if cart item has set course items
            if (parseInt(this.gid) === 256 && setCourseItems !== null) {
              // check cart item's course item id includes in category item's course item id
              let valid_course_item_ids = []
              category_item.courses.forEach(cc => {
                cc.course_items.forEach(ccc => { valid_course_item_ids.push(ccc.course_item_id) })
              })

              setCourseItems.forEach(sci => {
                if (!valid_course_item_ids.includes(sci.setCourseItemID)) { notAvailableItems.push(item.name) }
              })
            }

            if (category_item.avail_schema && checkAvailableSchema(category_item.avail_schema, currentTime) !== 0) notAvailableItems.push(item.name)
          }
        })
      }

      if (notAvailableItems.length > 0) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: this.$t('item_unavailable'),
          text: 'The following item(s) is unavailable:' + notAvailableItems.join(', '),
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }

      if (this.mode.isDelivery) { await this.$store.dispatch('delivery/fetchPresetItem') }
      await this.$store.dispatch('cart/checkCartItems')
      if (this.cartMsg) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to checkout',
          text: this.cartMsg,
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }
      if (this.discountInfoIsFilled && this.discountInfo) {
        if (this.discounts.length === 0) {
          this.$store.dispatch('cart/resetDiscountInfo')
        }
        let q = this.discounts.find(v => v.id === this.discountInfo.bill_member_voucher_id)
        if (q && !q.gems_member_voucher.UnlimitedUse && q.used) this.$store.dispatch('cart/resetDiscountInfo')
      }

      if (this.posType === 0) {
        await this.$store.dispatch('order/checkOrder')
      } else {
        await this.$store.dispatch('order/calculateOrder')
      }

      if (this.orderMsg) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to checkout',
          text: this.orderMsg,
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      } else {
        if (this.isMember) {
          await this.$store.dispatch('voucher/fetchMyVouchers')
        }
        if (this.paymentMode && (!this.paymentModes.find(p => p.code === this.paymentMode.code) && !this.noPaymentModes.find(p => p.code === this.paymentMode.code))) {
          this.$store.dispatch('order/resetPaymentMode')
        }
        this.$bus.$emit('showFullLoading', false)
        this.$router.push({ name: 'checkout' })
      }
    },
    async submit () {
      if (this.mode.isDinein && Array.isArray(this.dineTimes) && this.dineTimes[0] === null) {
        await this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to submit',
          text: 'Our last order for the day has ended. If there\'s any enquiries please approach any of our staff. Thank you',
          modal: true
        })

        return;
      }

      if (this.rInfoExpiredAlert()) {
        await this.$store.dispatch('responseMessage', {
          type: 'error',
          text: 'Session Expired',
          noBtn: this.$mq === 'sm' || this.$mq === 'md' || this.$mq === 'lg' || this.$mq === 'xl'
        })
        await this.$store.dispatch('resetAppRelatedData')
        await this.$store.dispatch('config/resetModeSelected')
        await this.$store.dispatch('fetchMembershipInfo')
        await this.$router.push({ name: 'welcome' })
      } else if (this.noTableOrAddressAlert()) {
        return
      }

      this.$router.push({ name: 'submit' })
    }
  },
}
</script>

<style scoped>
.btn {
  margin: 10px;
}
.nowrap-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/CheckoutFooter.vue